import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { PAYMENTS_MANAGEMENT } from "constants/action-types";
import siteConfig from "site-config";
import { updateEntities } from "redux-query";
import { stringify } from "querystring";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import { Chargebacks as ChargebacksSchema, Chargeback as ChargebackSchema } from "schemas/payments";
import { push } from "react-router-redux";
import { add as snackbarAdd } from "actions/snackbars";
import { format } from "lib/globalize-util";

export const fetchChargebacksEpic = (action$, store) =>
    action$.ofType(PAYMENTS_MANAGEMENT.CHARGEBACKS.FETCH.START).mergeMap(({ payload }) => {
        const { scope, chargebacks } = store.getState();
        const query = {
            status: chargebacks.status,
            __populate: ["__evidence", "__reasons"],
            __meta: true,
        };

        return ajax(`${siteConfig.api}/api/v3.0/chargebacks/currency_code/${payload.currencyCode}?${stringify(query)}`)
            .flatMap(({ response, status, xhr }) => {
                const chargebackRecords = getRecordsFromSchema(ChargebacksSchema);
                const data = normalizeData(response, ChargebacksSchema, chargebackRecords);

                return [
                    updateEntities({
                        chargebacks: (current) => {
                            return {
                                ...current,
                                ...data.entities.chargeback,
                            };
                        },
                    }),
                    {
                        payload: data.result,
                        type: PAYMENTS_MANAGEMENT.CHARGEBACKS.FETCH.COMPLETED,
                    },
                ];
            })
            .catch((error) => {
                const actions = [];

                if (error.status === 403) {
                    actions.push(Observable.of(push("/403")));
                }

                return Observable.merge(
                    ...actions,
                    Observable.of({
                        error: true,
                        payload: error,
                        type: PAYMENTS_MANAGEMENT.CHARGEBACKS.FETCH.COMPLETED,
                    })
                );
            });
    });

export const fetchChargebackEpic = (action$, store) =>
    action$.ofType(PAYMENTS_MANAGEMENT.CHARGEBACK.FETCH.START).mergeMap(({ payload }) => {
        const query = {
            __populate: ["__evidence", "__reasons"],
            __meta: true,
        };

        return ajax(`${siteConfig.api}/api/v3.0/chargeback/${payload}?${stringify(query)}`)
            .flatMap(({ response, status, xhr }) => {
                const chargebackRecord = getRecordsFromSchema(ChargebackSchema);
                const data = normalizeData(response, ChargebackSchema, chargebackRecord);

                return [
                    updateEntities({
                        chargebacks: (current) => {
                            return {
                                ...current,
                                ...data.entities.chargeback,
                            };
                        },
                    }),
                    {
                        payload: [data.result],
                        type: PAYMENTS_MANAGEMENT.CHARGEBACK.FETCH.COMPLETED,
                    },
                ];
            })
            .catch((error) => {
                return Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        type: PAYMENTS_MANAGEMENT.CHARGEBACK.FETCH.COMPLETED,
                    })
                );
            });
    });

export const updateChargebackWithEvidenceEpic = (action$, store) =>
    action$.ofType(PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.CREATE.COMPLETED).mergeMap(({ payload, error }) => {
        if (error) {
            const message =
                payload.message ||
                format("Something went wrong uploading your evidence. Please refresh and try again.");
            return [snackbarAdd({ message })];
        }

        const chargebackRecord = getRecordsFromSchema(ChargebackSchema);
        const data = normalizeData(payload, ChargebackSchema, chargebackRecord);

        return [
            updateEntities({
                chargebacks: (current) => {
                    return {
                        ...current,
                        ...data.entities.chargeback,
                    };
                },
            }),
            snackbarAdd({ message: format("Evidence uploaded") }),
        ];
    });

export const deleteChargebackEvidenceEpic = (action$, store) =>
    action$.ofType(PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.DELETE.START).mergeMap(({ payload }) => {
        return ajax({
            url: `${siteConfig.api}/api/v3.0/chargeback/${payload.chargebackId}/evidence/${payload.id}`,
            method: "DELETE",
        })
            .flatMap(({ response, status, xhr }) => {
                const chargebackRecord = getRecordsFromSchema(ChargebackSchema);
                const data = normalizeData(response, ChargebackSchema, chargebackRecord);

                return [
                    updateEntities({
                        chargebacks: (current) => {
                            return {
                                ...current,
                                ...data.entities.chargeback,
                            };
                        },
                    }),
                    {
                        type: PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.DELETE.COMPLETED,
                    },
                ];
            })
            .catch((error) => {
                return Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        type: PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.DELETE.COMPLETED,
                    })
                );
            });
    });

export const submitEvidenceToAdyenEpic = (action$, store) =>
    action$.ofType(PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.SUBMIT.START).mergeMap(({ payload }) => {
        return ajax({
            url: `${siteConfig.api}/api/v3.0/chargeback/${payload}/evidence/submit`,
            method: "POST",
        })
            .map(({ response, status }) => {
                if (response && status === 200) {
                    const chargebackRecord = getRecordsFromSchema(ChargebackSchema);
                    const data = normalizeData(response, ChargebackSchema, chargebackRecord);
                    return { entities: data.entities, result: [data.result], error: false };
                } else {
                    return { error: true, entities: null, result: [] };
                }
            })
            .flatMap(({ entities, result, error }) => {
                if (error) {
                    return [
                        store.dispatch(
                            snackbarAdd({ message: "Oops! Something went wrong. Please refresh and try again." })
                        ),
                        {
                            payload: [],
                            error: true,
                            type: PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.SUBMIT.COMPLETED,
                        },
                    ];
                } else {
                    return [
                        updateEntities({
                            chargebacks: (current) => {
                                return {
                                    ...current,
                                    ...entities.chargeback,
                                };
                            },
                        }),
                        {
                            payload: result,
                            type: PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.SUBMIT.COMPLETED,
                        },
                        snackbarAdd({ message: format("Evidence uploaded") }),
                    ];
                }
            })
            .catch((error) => {
                return Observable.merge(
                    Observable.of(
                        snackbarAdd({ message: format("Oops! Something went wrong. Please refresh and try again.") })
                    ),
                    Observable.of({
                        error: true,
                        payload: error,
                        type: PAYMENTS_MANAGEMENT.CHARGEBACK.EVIDENCE.SUBMIT.COMPLETED,
                    })
                );
            });
    });
